import { ArrayHelpers, FormikProps } from "formik";
import { useAtom } from "jotai";
import _ from "lodash";
import { useContext, useMemo } from "react";
import { AvisContext } from "../../../../context";
import { AvisPrimaryButton } from "../../../buttons";
import {
  atomIsModalOpen,
  atomModalEditItemIndex,
  atomRowKeys,
} from "../../atoms";
import {
  IAvisEditableListAddEditConfig,
  IEditableListRowState,
} from "../../types";

interface IAvisEditableListAddButtonProps<
  T extends object,
  P extends IEditableListRowState,
> {
  initValue: P;
  bag: FormikProps<T>;
  bagField: keyof T;
  disabled?: boolean;
  loading?: boolean;
  customButtonAddText?: string;
  maxRows?: number;
  hideEdit?: boolean;
  customAddEditConfig?: IAvisEditableListAddEditConfig<P>;
  arrayHelpers: ArrayHelpers;
}

export function AvisEditableListAddButton<T extends object, P extends object>(
  props: Readonly<IAvisEditableListAddButtonProps<T, P>>,
) {
  const { res } = useContext(AvisContext);

  const {
    bag,
    bagField,
    disabled,
    loading,
    initValue,
    customButtonAddText,
    maxRows,
    customAddEditConfig,
    arrayHelpers,
  } = props;

  const [, setRowKeys] = useAtom(atomRowKeys);
  const [, setModalEditItemIndex] = useAtom(atomModalEditItemIndex);
  const [, setIsModalOpen] = useAtom(atomIsModalOpen);

  const bagFieldArray = useMemo(
    () => bag.values[bagField] as P[],
    [bag, bagField],
  );

  const addEnabled = useMemo(
    () => (maxRows ? bagFieldArray.length < maxRows : true),
    [bagFieldArray, maxRows],
  );

  if (disabled) return null;

  return (
    <AvisPrimaryButton
      className="avis-editable-list-add-button"
      label={customButtonAddText ?? res("button_Add")}
      spacing="small"
      disabled={!addEnabled || loading}
      onClick={() => {
        //1. Custom setModalOpen defined - just set state to "true"
        if (customAddEditConfig?.customSetModalOpen) {
          customAddEditConfig.customSetModalOpen(true);
          return;
        }

        //2. Custom ModalForm open - Specific form for single row edit
        if (
          customAddEditConfig?.renderForm &&
          customAddEditConfig.validationSchema
        ) {
          setModalEditItemIndex(undefined);
          setIsModalOpen(true);
          return;
        }

        //3. Default list add row behaviour - adds new row with initValue
        const newKey = _.uniqueId();
        setRowKeys((oldArray) => (oldArray ? [...oldArray, newKey] : [newKey]));

        arrayHelpers.push({ ...initValue, rowState: "Add" } as P);
      }}
    />
  );
}
